import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';

export default function BankDetailsModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    bank_account_name: '',
    sort_code: '',
    account_number: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
  };

  const handleSortCodeChange = (e) => {
    setForm(prev => ({ ...prev, sort_code: formatSortCode(e.target.value) }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_account_name.trim()) {
      setError('Account name is required');
      return;
    }
    const sortDigits = form.sort_code.replace(/\D/g, '');
    if (sortDigits.length !== 6) {
      setError('Sort code must be 6 digits (e.g. 12-34-56)');
      return;
    }
    if (!/^\d{8}$/.test(form.account_number)) {
      setError('Account number must be exactly 8 digits');
      return;
    }
    setSaving(true);
    try {
      await onSave(employee.id, form);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = form.bank_account_name.trim() !== '' &&
    form.sort_code.replace(/\D/g, '').length === 6 &&
    /^\d{8}$/.test(form.account_number);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Change Bank Details - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {employee.bank_account_name && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
              <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Current Details</p>
              <p className="text-gray-600 dark:text-gray-400">Account name: {employee.bank_account_name}</p>
              <p className="text-gray-600 dark:text-gray-400">Sort code: {employee.sort_code ? `****${employee.sort_code.replace(/\D/g, '').slice(-2)}` : '—'}</p>
              <p className="text-gray-600 dark:text-gray-400">Account number: {employee.account_number ? `****${employee.account_number.slice(-4)}` : '—'}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Name *</label>
              <input type="text" name="bank_account_name" value={form.bank_account_name} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="e.g. John Smith" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sort Code *</label>
                <input type="text" name="sort_code" value={form.sort_code} onChange={handleSortCodeChange} inputMode="numeric"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="12-34-56" maxLength="8" />
                {form.sort_code && form.sort_code.replace(/\D/g, '').length > 0 && form.sort_code.replace(/\D/g, '').length < 6 && (
                  <p className="text-xs text-red-500 mt-1">Sort code must be 6 digits</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Number *</label>
                <input type="text" name="account_number" value={form.account_number} onChange={handleChange} inputMode="numeric"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="12345678" maxLength="8" />
                {form.account_number && form.account_number.length > 0 && !/^\d{8}$/.test(form.account_number) && (
                  <p className="text-xs text-red-500 mt-1">Account number must be exactly 8 digits</p>
                )}
              </div>
            </div>

            {hasChanged && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                Bank details will be updated and flagged for payroll processing.
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving || !hasChanged}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Update Bank Details'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
